// Copyright (c) 2020 WiseTime. All rights reserved.

import React from "react"
import styled from "styled-components/macro"
import TagSuggestionLabel from "./TagSuggestionLabel"
import TagLabel from "./TagLabel"
import ActivityTypeLabel from "./ActivityTypeLabel"
import TagCategoriesDropdown, {
  TAG_CATEGORIES_DROPDOWN_CLASSNAME,
  TAG_CATEGORIES_DROPDOWN_HEADER_CLASSNAME,
} from "./TagCategoriesDropdown"
import { DestinationOneOf } from "features/shared/search-dropdown/types"
import { TagRef } from "generated/wisetime/user/type/tag_ref_pb"
import { TagSuggestion } from "generated/wisetime/user/timeline/v4/timeline_pb"
import { ActivityTypeRef } from "generated/wisetime/user/type/activity_type_ref_pb"
import { TagCategoryRef } from "../../../generated/wisetime/user/type/tag_category_ref_pb"

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: .2rem 0 .6rem;
  max-width: 50%;
  
  > * {
    margin: .4rem 0 0 .4rem;
  }
  
  @media (min-width: ${({ theme }): string => theme.screen.md}) {
    justify-content: flex-end;
  }
  
  @media (max-width: ${({ theme }): string => theme.screen.md}) {
    order: 10;
    max-width: none;
    flex-basis: 100%;
    padding-top: 2px;
    display: flex;

    > * {
      margin: 0 .4rem .4rem 0;

      &:not(${`.${TAG_CATEGORIES_DROPDOWN_CLASSNAME}`}) {
        font-weight: 400;
        line-height: 17px;
        height: 18px;
        padding: 0 .5rem;
      }

      @media (max-width: ${({ theme }): string => theme.screen.sm }) {
        max-width: 130px;
      }
    }

    ${`.${TAG_CATEGORIES_DROPDOWN_HEADER_CLASSNAME}`} {
      font-weight: 400;
      line-height: 18px;
      height: 18px;
    }
  }
`

type Props = {
  tagRefs: TagRef.AsObject[],
  activityTypeRefs: ActivityTypeRef.AsObject[],
  timeGroupIdOrTimeRowsIds: DestinationOneOf,
  tagSuggestions?: TagSuggestion.AsObject[],
  tagCategoriesList?: TagCategoryRef.AsObject[],
  className?: string,
  isDisabled?: boolean,
  isDisabledGroup?: boolean,
  tagWithOverlay?: boolean,
}

const TagList = ({
  tagRefs,
  activityTypeRefs,
  timeGroupIdOrTimeRowsIds,
  tagSuggestions = [],
  tagCategoriesList = [],
  className,
  isDisabled = false,
  isDisabledGroup = false,
  tagWithOverlay = false,
}: Props): React.ReactElement<Props> => {
  return (
    <Tags className={className}>
      {!isDisabled &&
      tagSuggestions.map((tagSuggestion: TagSuggestion.AsObject) => (
        <TagSuggestionLabel
          key={tagSuggestion.id}
          timeRowId={timeGroupIdOrTimeRowsIds.toString()}
          tagSuggestion={tagSuggestion}
        />
      ))}
      {tagRefs.map((tagRef: TagRef.AsObject) => (
        <TagLabel
          timeGroupIdOrTimeRowsIds={timeGroupIdOrTimeRowsIds}
          key={tagRef.id}
          withOverlay={tagWithOverlay}
          isDisabled={isDisabled}
          isDisabledGroup={isDisabledGroup}
          tagRef={tagRef}
          addedTagIds={tagRefs.map(t => t.id)}
        />
      ))}
      {activityTypeRefs.map((activityTypeRef: ActivityTypeRef.AsObject) => (
        <ActivityTypeLabel
          key={activityTypeRef.id}
          timeGroupIdOrTimeRowsIds={timeGroupIdOrTimeRowsIds}
          isDisabled={isDisabled}
          isDisabledGroup={isDisabledGroup}
          activityTypeRef={activityTypeRef}
        />
      ))}
      {tagCategoriesList.map((tagCategoryRef: TagCategoryRef.AsObject) => (
        <TagCategoriesDropdown
          key={tagCategoryRef.id}
          tagCategoryId={tagCategoryRef.id}
          tagCategoryLabel={tagCategoryRef.label}
          timeGroupIdOrTimeRowsIds={timeGroupIdOrTimeRowsIds}
        />
      ))}
    </Tags>
  )
}

export default TagList
